import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import qrcode from 'qrcode-generator';
import type { ElicitationAnswer, ElicitationRequest, Receipt, ToolOutcome } from '@rally/agent';
import { api, setDemoKey } from '../api.ts';
import type { DemoPlan, DemoState } from '../api.ts';
import { RallyHost } from '../host.ts';
import type { HostEvent } from '../host.ts';
import { McpAppFrame } from '../components/McpAppFrame.tsx';
import { useVoice } from '../components/useVoice.ts';
import type { VoiceMode } from '../components/useVoice.ts';

type Line = { role: 'user' | 'alexa' | 'system'; text: string; speaker?: string; meta?: string };
type ToolRow = { id: string; name: string; args: Record<string, unknown>; outcome?: ToolOutcome };
/** The Alexa+ wallet sheet, over an open UCP checkout session. */
type Wallet = { phase: 'closed' } | { phase: 'pin' | 'processing' | 'done'; checkoutId: string; amountCents: number; summary: string };
type Settings = { era: 'legacy' | 'auto'; planner: 'offline' | 'llm'; size: 'show8' | 'show15' | 'voice'; theme: 'dark' | 'light'; speaker: 'lin' | 'david' | 'visitor' | 'anyone'; voice: VoiceMode };
type Tab = 'story' | 'phones' | 'agents' | 'memory' | 'messages' | 'protocol' | 'latency';

// "visitor" is a voice the household doesn't know: Rally treats it like the person a surprise is for.
// "anyone" is Alexa+ today: no speaker identity reaches the add-on at all.
const SPEAKERS = { lin: 'Lin (organizer)', david: 'David (birthday)', visitor: 'Someone else (unrecognized)', anyone: 'Anyone (no voice ID)' } as const;

type Step = { say?: string; as?: Settings['speaker']; action?: 'friends' | 'morning' | 'partyweek' | 'partyday' | 'after' | 'organizer'; label: string; note: string };

const STORY: Step[] = [
  { say: "Alexa, ask Rally to plan a surprise party for David's 60th next weekend", label: 'Start the surprise', note: 'Rally asks who to invite.' },
  { say: 'The family, his golf buddies and Priya', label: 'Who’s invited', note: 'Draft around David’s calendar; secret from him.' },
  { say: 'Yes', label: 'Send the invites', note: 'Confirmed on screen by your spoken yes.' },
  { action: 'friends', label: 'Friends answer on their phones', note: 'Watch the card fill in. Or open a guest link yourself.' },
  { action: 'morning', label: 'Next morning', note: 'Days later, from any device.' },
  { say: "How's David's party going?", label: 'Check in', note: 'Answered from memory, with a proactive nudge.' },
  { say: 'Yes', label: 'Nudge Sam', note: 'Sam answers after the reminder.' },
  { say: "How's it going now?", label: 'Check again', note: 'Everyone’s in: places that fit everyone.' },
  { say: 'Yes', label: 'Hear the places', note: 'Checked against needs guests declared themselves.' },
  { say: 'Book the first one', label: 'Book it', note: 'Rally reads the booking back, deposit included.' },
  { say: 'Yes', label: 'Confirm the booking', note: 'Reservation partner, calendar holds, deposits.' },
  { say: 'Pay for this order', label: 'Pay my deposit', note: 'Tap Pay on the card: the Amazon Pay sheet (sandbox, any 4 digits).' },
  { action: 'partyweek', as: 'david', say: 'What am I doing Saturday?', label: 'David asks', note: 'Same Echo, different speaker: honest secrecy.' },
  { action: 'partyday', as: 'lin', say: "Tell everyone David's leaving the house at 6:30, so please arrive by 6:45", label: 'Day of', note: 'Read back before it goes out.' },
  { say: 'Yes', label: 'Send it', note: 'Everyone but David gets it.' },
  { say: "How's tonight looking?", label: 'Day-of brief', note: 'Who’s coming, deposits, live weather.' },
  { action: 'after', as: 'david', say: 'So what was the secret about Saturday?', label: 'After the party', note: 'The secret lifts: David hears who planned it.' },
  { say: 'Yes', label: 'Say thank you', note: 'David thanks everyone who came, in his own name.' },
];

// Alexa+ doesn't tell add-ons who is speaking yet. Rally still keeps the surprise: nothing plays on the Echo.
const STORY_TODAY: Step[] = [
  { as: 'anyone', say: "Alexa, ask Rally to plan a surprise party for David's 60th next weekend", label: 'Start the surprise', note: 'This Echo can’t tell Lin’s voice from David’s.' },
  { say: 'The family, his golf buddies and Priya', label: 'Who’s invited', note: 'Rally keeps it off the speaker and texts Lin a private link.' },
  { action: 'organizer', label: 'Open Lin’s private link', note: 'Her phone: the same card, tools and confirmations, acting as Lin. Send the invites there.' },
  { say: "What's planned next weekend?", label: 'Anyone asks the Echo', note: 'Everyone, Lin included, hears only the secrecy line here.' },
];

function useHousehold(): [string | null, () => Promise<void>] {
  const [hh, setHh] = useState<string | null>(null);
  const create = useCallback(async () => {
    const { householdId, key } = await api.newDemo();
    setDemoKey(key);
    try {
      localStorage.setItem('rally.household', householdId);
      localStorage.setItem('rally.key', key);
    } catch {
      /* private mode */
    }
    setHh(householdId);
  }, []);
  useEffect(() => {
    let saved: string | null = null;
    let key: string | null = null;
    try {
      saved = localStorage.getItem('rally.household');
      key = localStorage.getItem('rally.key');
    } catch {
      /* private mode */
    }
    // A sandbox saved before keys existed can't be opened any more; start a fresh one.
    if (saved && key) {
      setDemoKey(key);
      setHh(saved);
    } else void create();
  }, [create]);
  return [hh, create];
}

export function Simulator() {
  const [settings, setSettings] = useState<Settings>({ era: 'auto', planner: 'offline', size: 'show8', theme: 'dark', speaker: 'lin', voice: 'browser' });
  const [household, newHousehold] = useHousehold();
  const [config, setConfig] = useState<{ llm: string | null; storage: string; serverless: boolean; version: string } | null>(null);
  const [skill, setSkill] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [tools, setTools] = useState<ToolRow[]>([]);
  const [ui, setUi] = useState<{ uri: string; toolName: string; data?: Record<string, any> } | null>(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState<{ request: ElicitationRequest; auto: boolean; answer: (a: ElicitationAnswer) => void } | null>(null);
  const [wallet, setWallet] = useState<Wallet>({ phase: 'closed' });
  const [turnMs, setTurnMs] = useState<number[]>([]);
  const [tab, setTab] = useState<Tab>('story');
  const [input, setInput] = useState('');
  const [state, setState] = useState<DemoState | null>(null);
  const [step, setStep] = useState(0);
  const [storyMode, setStoryMode] = useState<'voice' | 'today'>('voice');
  const story = storyMode === 'today' ? STORY_TODAY : STORY;
  const [autoFriends, setAutoFriends] = useState(true);
  const [live, setLive] = useState<string[]>([]);
  const hostRef = useRef<RallyHost | null>(null);
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    void api.config().then(setConfig).catch(() => setConfig(null));
    void api.skill().then(setSkill);
  }, []);

  const [lost, setLost] = useState(false);
  const hadPlans = useRef(false);
  const refresh = useCallback(async () => {
    if (!household) return;
    try {
      const next = await api.state(household);
      // In-memory storage on a serverless host resets when a new instance
      // starts. Say so plainly instead of letting the story fail oddly.
      if (hadPlans.current && next.plans.length === 0) setLost(true);
      hadPlans.current = next.plans.length > 0;
      setState(next);
    } catch (err) {
      if (/Unknown demo household/.test((err as Error).message)) await newHousehold();
    }
  }, [household, newHousehold]);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 2500);
    return () => clearInterval(t);
  }, [refresh]);

  const friendsReply = useCallback(async () => {
    if (!household) return;
    const { answered } = await api.simulate(household, liveRef.current);
    if (answered.length) setLines((l) => [...l, { role: 'system', text: `${answered.length} ${answered.length === 1 ? 'friend' : 'friends'} answered on their phones.` }]);
    await refresh();
  }, [household, refresh]);

  const voice = useVoice((text) => void send(text), settings.voice);
  const voiceRef = useRef(voice);
  voiceRef.current = voice;

  const onHostEvent = useCallback(
    (e: HostEvent) => {
      switch (e.type) {
        case 'user':
          setLines((l) => [...l, { role: 'user', text: e.text, speaker: settingsRef.current.speaker }]);
          break;
        case 'alexa':
          setLines((l) => [...l, { role: 'alexa', text: e.text, meta: `${e.planner} · ${e.latencyMs} ms` }]);
          setCaption(e.text);
          setTurnMs((xs) => [...xs.slice(-49), e.latencyMs]);
          voiceRef.current.speak(e.text);
          break;
        case 'system':
          setLines((l) => [...l, { role: 'system', text: e.text }]);
          break;
        case 'receipt':
          setReceipts((r) => [...r.slice(-199), e.receipt]);
          break;
        case 'agent': {
          const ev = e.event;
          if (ev.type === 'tool_call') setTools((t) => [...t.slice(-99), { id: `${Date.now()}-${ev.id}`, name: ev.name, args: ev.args }]);
          if (ev.type === 'tool_result')
            setTools((t) => {
              const i = [...t].reverse().findIndex((r) => r.name === ev.name && !r.outcome);
              if (i < 0) return t;
              const idx = t.length - 1 - i;
              return t.map((r, j) => (j === idx ? { ...r, outcome: ev.outcome } : r));
            });
          if (ev.type === 'ui') setUi({ uri: ev.resourceUri, toolName: ev.toolName, data: ev.data });
          if (ev.type === 'tool_result' && !ev.outcome.isError) {
            if (ev.name === 'send_invites' && ev.outcome.data?.sent && autoRef.current) setTimeout(() => void friendsReplyRef.current(), 3000);
            if (ev.name === 'nudge_guests' && ev.outcome.data?.nudged && autoRef.current) setTimeout(() => void friendsReplyRef.current(), 700);
            void refreshRef.current();
          }
          break;
        }
        case 'elicitation':
          setSheet({ request: e.request, auto: e.auto, answer: e.answer });
          // A command isn't a yes: Alexa reads back what will happen and waits.
          if (!e.auto) {
            setLines((l) => [...l, { role: 'alexa', text: e.request.message, meta: 'read-back · say yes or tap Confirm' }]);
            setCaption(e.request.message);
            voiceRef.current.speak(e.request.message);
          }
          break;
        case 'elicitation_done':
          setSheet(null);
          break;
        case 'busy':
          setBusy(e.on);
          if (e.on) setCaption('');
          break;
      }
    },
    [],
  );
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const autoRef = useRef(autoFriends);
  autoRef.current = autoFriends;
  const friendsReplyRef = useRef(friendsReply);
  friendsReplyRef.current = friendsReply;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  // Bumped on every speaker change: work that started for the previous speaker (a payment, a
  // checkout opening) must not put anything on the screen for the next one.
  const speakerEpoch = useRef(0);

  // One host per household + speaker + protocol + brain. Switching speaker keeps the transcript, but
  // clears the screen: the next person may be the one a surprise is kept from. That includes any
  // sheet still waiting on the previous speaker: a confirmation is cancelled, and an open checkout
  // is cancelled so nothing is charged.
  useEffect(() => {
    speakerEpoch.current += 1;
    setUi(null);
    setCaption('');
    sheetRef.current?.answer({ action: 'cancel' });
    setSheet(null);
    const w = walletRef.current;
    if (w.phase === 'pin') void api.cancelCheckout(w.checkoutId);
    setWallet({ phase: 'closed' });
    voiceRef.current.stopSpeaking(false);
  }, [settings.speaker]);
  useEffect(() => {
    if (!household) return;
    let current = true;
    const host = new RallyHost({
      household,
      speaker: settings.speaker === 'anyone' ? '' : settings.speaker,
      era: settings.era,
      planner: settings.planner,
      llmLabel: config?.llm,
      skill,
      onEvent: (e) => {
        // A replaced host can still be finishing a turn. What it would say or show belonged to the
        // previous speaker, so none of it reaches the screen or the speaker.
        if (!current) {
          if (e.type === 'elicitation') return e.answer({ action: 'cancel' });
          if (e.type === 'alexa' || (e.type === 'agent' && e.event.type === 'ui')) return;
        }
        onHostEvent(e);
      },
    });
    hostRef.current = host;
    void host.client.listTools().catch((err: Error) => setLines((l) => [...l, { role: 'system', text: `Could not reach Rally: ${err.message}` }]));
    return () => {
      current = false;
      void host.close();
      if (hostRef.current === host) hostRef.current = null;
    };
  }, [household, settings.speaker, settings.era, settings.planner, config?.llm, skill, onHostEvent]);

  const send = useCallback(async (text: string) => {
    const host = hostRef.current;
    if (!host || !text.trim()) return;
    if (/^pay for this order$/i.test(text.trim())) return void openWalletRef.current();
    await host.say(text.trim());
  }, []);

  const plan: DemoPlan | undefined = state?.plans[0];

  /** Alexa's own line on the Echo (not Rally's): shown, spoken, and kept in the transcript. */
  function alexaSays(text: string, meta: string) {
    setLines((l) => [...l, { role: 'alexa', text, meta }]);
    setCaption(text);
    voiceRef.current.speak(text);
  }

  // "Pay for this order" is Alexa+'s own checkout, so the host handles it. The wallet opens only for
  // the organizer's own deposit; anyone else hears the same neutral line whether or not a deposit
  // exists, so asking reveals nothing.
  async function openWallet() {
    if (walletRef.current.phase !== 'closed') return;
    const due = plan?.card?.venue?.hostDeposit as { status: string; amountCents: number } | undefined;
    if (!household || !plan || plan.organizer.id !== settings.speaker || due?.status !== 'due') return alexaSays('There’s nothing to pay for right now.', 'Alexa+ wallet');
    const epoch = speakerEpoch.current;
    const checkout = await api.openCheckout(`deposit~${household}~${plan.id}~host`, plan.organizer.name.split(' ')[0]!);
    if (epoch !== speakerEpoch.current) {
      if (!('error' in checkout)) void api.cancelCheckout(checkout.id);
      return;
    }
    if ('error' in checkout) return void setLines((l) => [...l, { role: 'system', text: checkout.error }]);
    // Built from the checkout itself: how many places the deposit covers and who is paid. Never who they are.
    const perGuest = Number(plan.card?.venue?.depositCentsPerGuest) || 0;
    const places = perGuest ? Math.round(checkout.totalCents / perGuest) : 0;
    const summary = `Deposit for ${places > 1 ? `${places} guests` : 'your place'}, paid to ${plan.card?.venue?.name ?? 'the venue'}.`;
    setWallet({ phase: 'pin', checkoutId: checkout.id, amountCents: checkout.totalCents, summary });
    voiceRef.current.speak('To pay with your Amazon wallet, say or enter your four digit voice code.');
  }
  const openWalletRef = useRef(openWallet);
  openWalletRef.current = openWallet;

  function closeWallet() {
    const w = walletRef.current;
    if (w.phase === 'pin') void api.cancelCheckout(w.checkoutId);
    setWallet({ phase: 'closed' });
  }

  async function payHostDeposit(pin: string) {
    const w = walletRef.current;
    if (!/^\d{4}$/.test(pin) || w.phase !== 'pin') return;
    const epoch = speakerEpoch.current;
    setWallet({ ...w, phase: 'processing' });
    const result = await api.completeCheckout(w.checkoutId);
    void refresh();
    // The speaker changed while it was paying: the sheet is already gone, and the Echo stays quiet.
    if (epoch !== speakerEpoch.current) {
      setLines((l) => [...l, { role: 'system', text: result.status === 'completed' ? 'The deposit went through after the speaker changed; the Echo stayed quiet.' : (result.error ?? 'Payment failed') }]);
      return;
    }
    if (result.status === 'completed') {
      setWallet({ ...w, phase: 'done' });
      alexaSays('Paid with your Amazon wallet. The venue has your deposit.', 'UCP checkout · Amazon Pay network token');
      setTimeout(() => setWallet((cur) => (cur.phase === 'done' ? { phase: 'closed' } : cur)), 1600);
    } else {
      setLines((l) => [...l, { role: 'system', text: result.error ?? 'Payment failed' }]);
      setWallet({ phase: 'closed' });
    }
  }

  async function jump(to: 'morning' | 'partyweek' | 'partyday' | 'after') {
    if (!household || !state) return;
    const now = new Date(state.now).getTime();
    const option = plan?.options.find((o) => o.id === (plan.chosenOptionId ?? plan.card?.options?.find((x: any) => x.leading)?.id)) ?? plan?.options[0];
    let hours = 15;
    if (to !== 'morning' && option) {
      const start = new Date(option.start).getTime();
      const target = to === 'partyweek' ? start - 2 * 86_400_000 - 8 * 3_600_000 : to === 'after' ? start + 15 * 3_600_000 : start - 3 * 3_600_000;
      hours = Math.max(0.1, (target - now) / 3_600_000);
    }
    await api.advance(household, Math.round(hours * 10) / 10);
    setLines((l) => [...l, { role: 'system', text: to === 'morning' ? '⏩ The next morning' : to === 'partyweek' ? '⏩ Two days before the party' : to === 'after' ? '⏩ The morning after the party' : '⏩ The day of the party, late afternoon' }]);
    await refresh();
  }

  async function runStep(i: number) {
    const s = story[i];
    if (!s) return;
    if (s.action === 'organizer') {
      const token = plan?.organizer.token;
      if (token) window.open(`${location.origin}/#/o/${token}`, '_blank', 'noopener');
      setTab('phones');
      setStep(i + 1);
      return;
    }
    if (s.action === 'friends') await friendsReply();
    else if (s.action) {
      await jump(s.action);
      // During party week, friends pay their own deposits from their links.
      if (s.action === 'partyweek') await friendsReply();
    }
    if (s.as && s.as !== settings.speaker) {
      setSettings((x) => ({ ...x, speaker: s.as! }));
      await new Promise((r) => setTimeout(r, 400));
    }
    // A later step (a spoken yes) can finish while this one waits on a confirmation.
    setStep((cur) => Math.max(cur, i + 1));
    if (s.say) await send(s.say);
  }

  async function startOver() {
    if (!household) return;
    closeWallet();
    sheetRef.current?.answer({ action: 'cancel' });
    await api.reset(household);
    setLines([]);
    setReceipts([]);
    setTools([]);
    setUi(null);
    setCaption('');
    setStep(0);
    setLive([]);
    setSettings((x) => ({ ...x, speaker: storyMode === 'today' ? 'anyone' : 'lin' }));
    const current = await api.state(household);
    if (current.clockOffsetHours) await api.advance(household, -current.clockOffsetHours);
    await refresh();
  }

  // The story list and transcript scroll inside the panel, so the Echo stays in view: keep the next
  // step and the newest line visible there without scrolling the page.
  const stepsRef = useRef<HTMLOListElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = stepsRef.current;
    const next = list?.querySelector('li.next');
    if (!list || !next) return;
    const l = list.getBoundingClientRect();
    const n = next.getBoundingClientRect();
    if (n.top < l.top) list.scrollTop -= l.top - n.top + 8;
    else if (n.bottom > l.bottom) list.scrollTop += n.bottom - l.bottom + 8;
  }, [step, storyMode, tab]);
  useEffect(() => {
    const t = transcriptRef.current;
    if (t) t.scrollTop = t.scrollHeight;
  }, [lines, tab]);

  const stats = useMemo(() => {
    const toolLat = tools.map((t) => t.outcome?.latencyMs).filter((x): x is number => typeof x === 'number').sort((a, b) => a - b);
    const p = (xs: number[], q: number) => (xs.length ? xs[Math.min(xs.length - 1, Math.floor(q * xs.length))] : undefined);
    const turns = [...turnMs].sort((a, b) => a - b);
    return {
      toolP50: p(toolLat, 0.5),
      toolP95: p(toolLat, 0.95),
      turnP50: p(turns, 0.5),
      calls: toolLat.length,
      under500: toolLat.length ? Math.round((100 * toolLat.filter((x) => x < 500).length) / toolLat.length) : undefined,
      bargeIn: voice.bargeInMs.length ? Math.max(...voice.bargeInMs) : undefined,
    };
  }, [tools, turnMs, voice.bargeInMs]);

  const nowLabel = state ? new Date(state.now).toLocaleString('en-US', { timeZone: state.timezone, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const deviceClass = settings.size === 'voice' ? 'device voice' : `device ${settings.size}`;

  return (
    <div className="sim">
      <section className="stage">
        <div className="toolbar">
          <label>
            Who’s talking <small>(speaker ID simulated)</small>
            <select value={settings.speaker} onChange={(e) => setSettings({ ...settings, speaker: e.target.value as Settings['speaker'] })}>
              {Object.entries(SPEAKERS).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Device
            <select value={settings.size} onChange={(e) => setSettings({ ...settings, size: e.target.value as Settings['size'] })}>
              <option value="show8">Echo Show 8</option>
              <option value="show15">Echo Show 15</option>
              <option value="voice">Voice only (Echo Dot)</option>
            </select>
          </label>
          <label>
            MCP protocol
            <select value={settings.era} onChange={(e) => setSettings({ ...settings, era: e.target.value as Settings['era'] })}>
              <option value="legacy">2025-11-25 (Alexa+ today)</option>
              <option value="auto">2026-07-28 (stateless)</option>
            </select>
          </label>
          <label>
            Alexa’s brain
            <select value={settings.planner} onChange={(e) => setSettings({ ...settings, planner: e.target.value as Settings['planner'] })}>
              <option value="offline">Offline rules</option>
              <option value="llm" disabled={!config?.llm}>
                {config?.llm ? `LLM (${config.llm.split(':')[0]})` : 'LLM (not configured)'}
              </option>
            </select>
          </label>
          <label>
            Alexa’s voice
            <select value={settings.voice} onChange={(e) => setSettings({ ...settings, voice: e.target.value as VoiceMode })} title="On-device: Moonshine listens and Kokoro speaks, in this browser. Nothing is uploaded.">
              <option value="browser">Browser speech</option>
              <option value="neural">On-device neural (150 MB once)</option>
            </select>
          </label>
          <div className="clock">
            <span>🕰 {nowLabel}</span>
            <button className="ghost small" onClick={() => void jump('morning')} disabled={!plan}>
              +1 morning
            </button>
            <button className="ghost small" onClick={() => void jump('partyday')} disabled={!plan}>
              Party day
            </button>
          </div>
        </div>

        <div className={deviceClass} data-theme={settings.theme}>
          <div className="bezel">
            <div className="screen">
              <div className="statusbar">
                <span>{state ? new Date(state.now).toLocaleTimeString('en-US', { timeZone: state.timezone, hour: 'numeric', minute: '2-digit' }) : ''}</span>
                <span className="badge-live">Rally add-on · {SPEAKERS[settings.speaker].split(' ')[0]} speaking</span>
              </div>
              <div className="canvas">
                {settings.size !== 'voice' && ui && hostRef.current ? (
                  <McpAppFrame
                    key={`${ui.uri}-${settings.speaker}`}
                    callTool={(name, args) => hostRef.current!.viewCallTool(name, args)}
                    readResource={(uri) => hostRef.current!.readResource(uri)}
                    resourceUri={ui.uri}
                    toolName={ui.toolName}
                    data={ui.data}
                    size={settings.size === 'show15' ? 'show15' : 'show8'}
                    theme={settings.theme}
                    onMessage={(text) => void send(text)}
                  />
                ) : (
                  <div className="idle">
                    <div className="idle-title">“Alexa, ask Rally to plan a surprise party.”</div>
                    <div className="idle-sub">Say it once. Rally gets the whole group to the same table.</div>
                  </div>
                )}
              </div>
              <div className={`caption ${busy && !sheet ? 'thinking' : ''}`}>{busy && !sheet ? '…' : caption}</div>
              <div className={`lightbar ${voice.listening ? 'listening' : busy ? 'thinking' : voice.speaking ? 'speaking' : ''}`} />
            </div>
          </div>
          {sheet && (
            <div className="sheet" role="dialog" aria-label="Confirm">
              <div className="sheet-card">
                <div className="sheet-kicker">Confirm with Rally</div>
                <p className="sheet-msg">{sheet.request.message}</p>
                {sheet.auto ? (
                  <div className="confirmed">✓ Confirmed by voice</div>
                ) : (
                  <div className="sheet-actions">
                    <button onClick={() => sheet.answer({ action: 'accept', content: { confirm: true } })}>Confirm</button>
                    <button className="ghost" onClick={() => sheet.answer({ action: 'decline' })}>
                      Not now
                    </button>
                  </div>
                )}
                <div className="sheet-hint">MCP elicitation{sheet.auto ? ' · answered from what you just said' : ' · or say “yes”'}</div>
              </div>
            </div>
          )}
          {wallet.phase !== 'closed' && <WalletSheet key={wallet.checkoutId} state={wallet.phase} summary={wallet.summary} amount={wallet.amountCents} onPay={payHostDeposit} onCancel={closeWallet} />}
        </div>

        <div className="controls">
          <button className={`mic ${voice.listening ? 'on' : ''}`} onClick={() => (voice.listening ? voice.stopListening() : voice.listen())} disabled={!voice.supported} title={voice.supported ? 'Push to talk' : 'Speech recognition needs Chrome or Edge; type instead'}>
            {voice.listening ? '● Listening' : '🎙 Talk'}
          </button>
          <form
            className="say"
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
              setInput('');
            }}
          >
            <input value={voice.interim || input} onChange={(e) => setInput(e.target.value)} placeholder={`Say or type to Alexa as ${SPEAKERS[settings.speaker].split(' ')[0]}…`} />
            <button type="submit" disabled={busy && !sheet}>
              Send
            </button>
          </form>
          <button className="ghost" onClick={() => voice.setMuted(!voice.muted)} title="Alexa's voice">
            {voice.muted ? '🔇' : '🔊'}
          </button>
        </div>
        {settings.voice === 'neural' && voice.status && (
          <p className="voice-status" role="status">
            {voice.status} Moonshine listens and Kokoro speaks, in this browser; nothing you say is uploaded.
          </p>
        )}
        <p className="fineprint">
          A simulated Alexa+ host (hackathon entrants can’t reach real Alexa+). Everything behind it is real: Rally’s MCP server over Streamable HTTP, its tools, MCP Apps views and checkout.
          {config ? ` Storage: ${config.storage === 'memory' && config.serverless ? 'in memory on a serverless host, so a quiet sandbox can reset' : config.storage}.` : ''}
        </p>
        {lost && (
          <p className="notice" role="status">
            The demo server restarted and this sandbox was reset (in-memory storage). Press <b>Start over</b> to run the story again.{' '}
            <button className="ghost small" onClick={() => setLost(false)}>OK</button>
          </p>
        )}
      </section>

      <aside className="hood">
        <div className="tabs">
          {(
            [
              ['story', 'Story'],
              ['phones', 'Guest phones'],
              ['agents', 'Agents'],
              ['memory', 'Memory'],
              ['messages', 'Messages'],
              ['protocol', 'Protocol'],
              ['latency', 'Latency'],
            ] as const
          ).map(([t, label]) => (
            <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'story' && (
          <div className="story">
            <div className="seg" role="group" aria-label="Which Alexa+">
              {(
                [
                  ['voice', 'With voice ID'],
                  ['today', 'Alexa+ today (no voice ID)'],
                ] as const
              ).map(([m, label]) => (
                <button
                  key={m}
                  className={storyMode === m ? '' : 'ghost'}
                  onClick={() => {
                    setStoryMode(m);
                    setStep(0);
                    setSettings((x) => ({ ...x, speaker: m === 'today' ? 'anyone' : 'lin' }));
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="muted">
              {storyMode === 'voice'
                ? 'Lin is planning a surprise 60th for her husband David, who shares the kitchen Echo Show. Click through, or say the lines yourself.'
                : 'Alexa+ doesn’t tell add-ons who is speaking yet. Rally still keeps the surprise: the Echo never plays it, and Lin plans on a private link. Press Start over first.'}
            </p>
            <ol ref={stepsRef}>
              {story.map((s, i) => (
                <li key={i} className={i < step ? 'done' : i === step ? 'next' : ''}>
                  <button className={i === step ? '' : 'ghost'} disabled={busy && !sheet} onClick={() => void runStep(i)}>
                    {i + 1}. {s.label}
                  </button>
                  <div className="step-note">
                    {s.say ? <q>{s.say}</q> : null} {s.note}
                  </div>
                </li>
              ))}
            </ol>
            <div className="row">
              <label className="check">
                <input type="checkbox" checked={autoFriends} onChange={(e) => setAutoFriends(e.target.checked)} /> Friends answer automatically
              </label>
              <button className="ghost small" onClick={() => void startOver()}>
                Start over
              </button>
            </div>
            <div className="transcript" ref={transcriptRef}>
              {lines.slice(-14).map((l, i) => (
                <div key={i} className={`line ${l.role}`}>
                  <b>{l.role === 'user' ? SPEAKERS[(l.speaker ?? 'lin') as keyof typeof SPEAKERS].split(' ')[0] : l.role === 'alexa' ? 'Alexa' : '·'}</b> {l.text}
                  {l.meta && <small> {l.meta}</small>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'phones' && (
          <div className="phones">
            <p className="muted">Each guest gets a private link. Open one on your phone and answer as them: the Echo Show card updates within seconds. Guests you open here stop auto-answering.</p>
            {plan?.organizer.token && (
              <div className="guest organizer-link">
                <div>
                  <b>{plan.organizer.name}</b> <span className="st in">organizer</span>
                  <div className="muted">Private planning link. It acts as {plan.organizer.name.split(' ')[0]}, because this Echo can’t tell voices apart.</div>
                  <div className="row">
                    <a href={`${location.origin}/#/o/${plan.organizer.token}`} target="_blank" rel="noreferrer">
                      Open {plan.organizer.name.split(' ')[0]}’s phone ↗
                    </a>
                  </div>
                </div>
                <Qr text={`${location.origin}/#/o/${plan.organizer.token}`} />
              </div>
            )}
            {!plan || !plan.guests.some((g) => g.token) ? (
              <p className="muted">Guest links appear once the invites go out.</p>
            ) : (
              plan.guests.map((g) => {
                const url = g.token ? `${location.origin}/#/r/${g.token}` : '';
                return (
                  <div key={g.personId} className={`guest ${g.status}`}>
                    <div>
                      <b>{g.name}</b> <span className={`st ${g.status}`}>{g.status === 'sent' ? 'waiting' : g.status}</span>
                      {url && (
                        <div className="row">
                          <a href={url} target="_blank" rel="noreferrer" onClick={() => setLive((xs) => [...new Set([...xs, g.personId])])}>
                            Open {g.name.split(' ')[0]}’s phone ↗
                          </a>
                        </div>
                      )}
                    </div>
                    {url && <Qr text={url} />}
                  </div>
                );
              })
            )}
          </div>
        )}

        {tab === 'agents' && (
          <div className="team">
            {(state?.team ?? []).map((m) => {
              const last = (plan?.events ?? []).filter((e) => e.actor === `agent:${m.id}`).at(-1);
              return (
                <div key={m.id} className={`member agent-${m.id}`}>
                  <b>{m.name}</b>
                  <span className="muted"> watches {m.watches}; writes {m.writes}.</span>
                  {last && <div className="last">{last.text}</div>}
                </div>
              );
            })}
            {plan?.card?.next?.label && <p className="next">Next: {plan.card.next.label}, after the organizer's yes.</p>}
          </div>
        )}
        {tab === 'agents' && (
          <ol className="timeline">
            {(plan?.events ?? []).slice().reverse().map((e, i) => (
              <li key={i} className={e.actor.replace(':', '-')}>
                <span className="who">{e.actor.startsWith('agent:') ? `${e.actor.slice(6)} agent` : e.actor}</span>
                <span className="when">{new Date(e.at).toLocaleString('en-US', { timeZone: state?.timezone, weekday: 'short', hour: 'numeric', minute: '2-digit' })}</span>
                <div>{e.text}</div>
              </li>
            ))}
            {!plan && <p className="muted">Nothing yet. Start the story.</p>}
          </ol>
        )}

        {tab === 'memory' && (
          <div className="memory">
            <p className="muted">
              What Rally has learned for this household, from {state?.memory.episodes.length ?? 0} past plans and what people told it. Each fact has two clocks: when it became true, and when Rally
              learned it. A change ends a fact instead of erasing it; forgetting erases it for good.
            </p>
            <ol className="facts">
              {(state?.memory.facts ?? [])
                .slice()
                .sort((a, b) => Number(Boolean(a.validTo || a.retiredAt)) - Number(Boolean(b.validTo || b.retiredAt)) || b.recordedAt.localeCompare(a.recordedAt))
                .map((f) => {
                  const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { timeZone: state?.timezone, month: 'short', day: 'numeric' });
                  const over = f.validTo || f.retiredAt;
                  return (
                    <li key={f.id} className={over ? 'ended' : ''}>
                      <div>{f.said.charAt(0).toUpperCase() + f.said.slice(1)}</div>
                      <div className="clocks">
                        <span>true since {day(f.validFrom)}</span>
                        {f.validTo && <span>ended {day(f.validTo)}</span>}
                        <span>learned {day(f.recordedAt)}</span>
                        {f.retiredAt && <span>no longer believed {day(f.retiredAt)}</span>}
                        <span className="src">{f.source === 'debrief' ? `debrief, ${f.plans} plans` : f.source === 'self' ? 'from them' : 'told by the organizer'}</span>
                        {f.hiddenFrom.length > 0 && <span className="hidden">hidden from {f.hiddenFrom.join(', ')}{f.hiddenUntil ? ` until ${day(f.hiddenUntil)}` : ''}</span>}
                      </div>
                    </li>
                  );
                })}
              {!state?.memory.facts.length && <p className="muted">Nothing learned yet.</p>}
            </ol>
          </div>
        )}

        {tab === 'messages' && (
          <ol className="outbox">
            {(plan?.outbox ?? []).slice().reverse().map((m) => (
              <li key={m.id}>
                <span className="who">To {m.to}</span> <span className="kind">{m.kind}</span>
                <div>{m.text}</div>
              </li>
            ))}
            {plan && <p className="muted">Nothing is ever sent to {plan.card?.secretFrom?.join(', ') || 'anyone the plan is secret from'}.</p>}
          </ol>
        )}

        {tab === 'protocol' && (
          <ol className="receipts">
            <p className="muted">Every JSON-RPC exchange between this Alexa+ host and Rally’s MCP server ({settings.era === 'auto' ? 'MCP 2026-07-28' : 'MCP 2025-11-25'}, Streamable HTTP). The card’s own refresh polling is hidden.</p>
            {receipts.slice().reverse().map((r, i) => (
              <li key={i} className={r.direction}>
                <span className="dir">{r.direction === 'out' ? '→' : '←'}</span>
                <span className="m">{r.method}</span>
                {r.ms !== undefined && <span className="ms">{r.ms} ms</span>}
                <div className="s">{r.summary}</div>
              </li>
            ))}
          </ol>
        )}

        {tab === 'latency' && (
          <div className="stats">
            <Stat label="Tool round-trip p50" value={stats.toolP50} unit="ms" target="< 500 ms (Alexa+ requirement)" />
            <Stat label="Tool round-trip p95" value={stats.toolP95} unit="ms" />
            <Stat label="Calls under 500 ms" value={stats.under500} unit="%" />
            <Stat label="Turn latency p50" value={stats.turnP50} unit="ms" target="planner + tools, before speech" />
            <Stat label="Barge-in (speech → silence)" value={stats.bargeIn} unit="ms" target="< 300 ms" />
            <Stat label="Tool calls" value={stats.calls} unit="" />
          </div>
        )}
      </aside>
    </div>
  );
}

function Qr({ text }: { text: string }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 3, margin: 2, scalable: true });
  }, [text]);
  return <div className="qr" aria-label="QR code for this guest's link" dangerouslySetInnerHTML={{ __html: svg }} />;
}

function Stat({ label, value, unit, target }: { label: string; value?: number; unit: string; target?: string }) {
  return (
    <div className="stat">
      <div className="stat-v">
        {value ?? '—'}
        {value !== undefined && <small>{unit}</small>}
      </div>
      <div className="stat-l">{label}</div>
      {target && <div className="stat-t">{target}</div>}
    </div>
  );
}

function WalletSheet({ state, summary, amount, onPay, onCancel }: { state: 'pin' | 'processing' | 'done'; summary: string; amount: number; onPay: (pin: string) => void; onCancel: () => void }) {
  const [pin, setPin] = useState('');
  return (
    <div className="sheet" role="dialog" aria-label="Amazon Wallet (simulated)">
      <div className="sheet-card wallet">
        <div className="sheet-kicker">Amazon Wallet · simulated</div>
        <div className="card-row">
          <span className="cardchip">Card</span> •••• 4242 {amount ? <span className="amt">${(amount / 100).toFixed(2)}</span> : null}
        </div>
        {state === 'done' ? (
          <div className="confirmed">✓ Paid to the venue</div>
        ) : (
          <>
            <p className="sheet-msg">{summary} Say or enter your 4-digit voice code. Alexa+ owns this step; Rally never sees card details.</p>
            <input className="pin" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} placeholder="••••" autoFocus />
            <div className="sheet-actions">
              <button disabled={state === 'processing' || pin.length !== 4} onClick={() => onPay(pin)}>
                {state === 'processing' ? 'Paying through UCP…' : 'Pay'}
              </button>
              <button className="ghost" onClick={onCancel} disabled={state === 'processing'}>
                Cancel
              </button>
            </div>
          </>
        )}
        <div className="sheet-hint">UCP checkout session · sandbox Amazon Pay token · names the venue as the merchant</div>
      </div>
    </div>
  );
}
